import { CustomCard, ControlBar } from '@/components';
import { OTElementId } from '@/constants';
import { getErrorFromAPI, handleToastMutation } from '@/helpers/toast';
import CustomAutocomplete from '@/pages/Settings/components/CourierMapping/components/AutoComplete';
import { useGetCourierMappingQuery, useGetListCourierQuery, useUpdateCourierSettingMutation } from '@/redux/api/api.caller';
import commonSlice from '@/redux/features/common.slice';
import settingsSlice, { courierMappingSelector } from '@/redux/features/settings.slice';
import { BlockStack, Button, Icon, SkeletonBodyText, Text } from '@shopify/polaris';
import { DeleteIcon, SearchIcon } from '@shopify/polaris-icons';
import { memo, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { ReactSortable } from 'react-sortablejs';
import { StyledCourierSettings } from './styled';

interface CourierSettingsProps {
  isWelcome?: boolean;
}

const CourierSettings = ({ isWelcome }: CourierSettingsProps) => {
  const fetchCourierMapping = useGetCourierMappingQuery();
  const { data, isLoading } = useGetListCourierQuery({ page: 1, perPage: 500 });
  const dispatch = useDispatch();
  const courierMapping = useSelector(courierMappingSelector);
  const [listCourier, setListCourier] = useState<Array<{ label: string; value: string }>>([]);
  const [courierSetting, courierSettingStatus] = useUpdateCourierSettingMutation();
  const [oldValue, setOldValue] = useState<Array<string> | undefined>([]);
  const [apiError, setApiError] = useState<string>('');

  useEffect(() => {
    if (fetchCourierMapping.status === 'fulfilled') {
      setOldValue(fetchCourierMapping.data?.data?.courierSettings || []);
      dispatch(settingsSlice.actions.handleCourierMapping({ courierList: fetchCourierMapping.data?.data?.courierSettings || [] }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchCourierMapping]);

  useEffect(() => {
    if (data) {
      const baseOptions = data.data.couriers.map((item) => {
        return {
          label: item.display_name,
          value: item.name,
        };
      });
      setListCourier(baseOptions);
    }
  }, [data]);

  const updateCourierSetting = () => {
    courierSetting({ payload: courierMapping.courierList }).then((res) => {
      if ('data' in res && res.data && res.data?.state === 1) {
        setOldValue(courierMapping.courierList);
        if (apiError) {
          setApiError('');
        }
        dispatch(commonSlice.actions.handleToast(handleToastMutation(res)));
        return;
      }
      setApiError(getErrorFromAPI(res));
    });
  };
  const handleSortList = (
    list: Array<{
      id: number;
      name: string;
    }>,
  ) => {
    const result = list.map((item) => item.name);
    dispatch(
      settingsSlice.actions.handleCourierMapping({
        ...courierMapping,
        courierList: result,
      }),
    );
  };
  const getSortList = useMemo(() => {
    const result = courierMapping.courierList?.map((courier, index) => {
      return {
        id: index + 1,
        name: courier,
      };
    });
    return result || [];
  }, [courierMapping.courierList]);
  const handleUpdateCourierSetting = (value: Array<string>) => {
    setApiError('');
    dispatch(
      settingsSlice.actions.handleCourierMapping({
        ...courierMapping,
        courierList: value,
      }),
    );
  };

  const handleDeleteCourierSettings = (id: number) => () => {
    const listCourierSettings = [...courierMapping.courierList];
    listCourierSettings.splice(id, 1);
    dispatch(
      settingsSlice.actions.handleCourierMapping({
        ...courierMapping,
        courierList: listCourierSettings,
      }),
    );
  };

  const actionGroup = [
    {
      content: 'Discard',
      onAction: () => {
        dispatch(settingsSlice.actions.handleCourierMapping({ courierList: oldValue as Array<string> }));
      },
      primary: false,
    },
    {
      content: 'Save',
      onAction: updateCourierSetting,
      primary: true,
      disabled: oldValue ? (oldValue?.toString() === courierMapping.courierList?.toString()) : true,
      loading: courierSettingStatus.isLoading,
    },
  ]

  return (
    <>
      {!isWelcome &&
        <ControlBar
          isFixed
          saveBarId={OTElementId.SaveBarCourierSettings}
          actionGroup={actionGroup}
          hidden={oldValue ? (oldValue?.toString() === courierMapping.courierList?.toString()) : true}
        />
      }
      <StyledCourierSettings>
        <CustomCard title="Frequently used couriers">
          <BlockStack gap="400">
            <Text as="p" variant="bodySm">
              When you have a new tracking number, the system will priortize detect couriers based on this list
            </Text>

            <BlockStack gap="200">
              <CustomAutocomplete
                placeholder="USPS, UPS,..."
                handleSelected={handleUpdateCourierSetting}
                label="Couriers list"
                baseOptions={listCourier}
                prefix={<Icon source={SearchIcon} tone="base" />}
                selected={courierMapping.courierList}
                loading={isLoading}
                allowMultiple
                error={apiError}
              />
              {isLoading ? (
                <SkeletonBodyText lines={5} />
              ) : (
                <ReactSortable list={getSortList} setList={handleSortList}>
                  {getSortList.map((item, index) => {
                    const courier = data?.data.couriers.find((courier) => courier.name === item.name);
                    return (
                      <div key={item.id} className="list-sortable">
                        <div className="left-item">
                          <Text as="h6" variant="headingSm">
                            {item.id}
                          </Text>
                          <img className="ml-16" src={courier?.logo || ''} alt={item.name} />
                          <div className="ml-16">{courier?.display_name || ''}</div>
                        </div>
                        <div className="delete-icon">
                          <Button icon={DeleteIcon} onClick={handleDeleteCourierSettings(index)} />
                        </div>
                      </div>
                    );
                  })}
                </ReactSortable>
              )}
            </BlockStack>
          </BlockStack>
        </CustomCard>
      </StyledCourierSettings>
    </>
  );
};

export default memo(CourierSettings);
